import { useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Coffee, Link2, Monitor, X } from 'lucide-react';
import AboutModal from '@components/layout/AboutModal';
import Badge from '@components/ui/Badge';
import Icon from '@components/ui/Icon';
import { lookupIcon } from '@components/ui/iconMap';
import { DEFAULT_PRESET_ID, ROOM_PRESETS, getPreset } from '@data/roomPresets';
import { useUIStore } from '@stores/useUIStore';
import { track } from '@utils/analytics';
import { calculateRT60 } from '@utils/calculateRT60';
import type { RT60Rating, RT60Result } from '@/types/analysis';

type CopyState = 'idle' | 'copied' | 'failed';

/** Octave bands matching `RT60Result.perBand` indices. */
const BAND_LABELS: readonly string[] = ['125', '250', '500', '1k', '2k', '4k'];

/** Visual cap for the per-band bars — anything above this fills the track. */
const BAR_MAX_RT60 = 1.5;

/** Verdict copy + accent color per rating bucket. Matches analysis panel logic. */
const RATING_INFO: Record<RT60Rating, { label: string; tone: string; color: string }> =
  {
    excellent: { label: 'Excellent', tone: 'green', color: 'var(--accent-green)' },
    good: { label: 'Good', tone: 'green', color: 'var(--accent-green)' },
    fair: { label: 'Acceptable', tone: 'amber', color: 'var(--accent-amber)' },
    poor: { label: 'Reverberant', tone: 'red', color: 'var(--accent-red)' },
  };

/**
 * MobileGate — read-only RT60 preview for sub-900px viewports.
 *
 * Mobile users can't drive the full editor (no precise drag, no room for
 * the wizard sidebar + viewport at once), but they CAN tap a preset and
 * see what a typical room sounds like. That gives them something to
 * engage with — a `mobile_preset_loaded` event fires per tap, breaking
 * the "single-pageview bounce" condition in Vercel Analytics — and a
 * concrete reason to come back on desktop.
 */
export default function MobileGate() {
  const showAbout = useUIStore((s) => s.showAbout);
  const [presetId, setPresetId] = useState<string>(DEFAULT_PRESET_ID);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const url =
    typeof window !== 'undefined' ? window.location.href : 'https://studioshell.app';
  const prettyUrl =
    typeof window !== 'undefined'
      ? (window.location.host + window.location.pathname).replace(/\/+$/, '') ||
        window.location.href
      : 'studioshell.app';

  const result = useMemo<RT60Result | null>(() => {
    const preset = getPreset(presetId);
    if (!preset?.dims || !preset?.materials) return null;
    return calculateRT60(preset.dims, preset.materials, []);
  }, [presetId]);

  function selectPreset(id: string) {
    if (id === presetId) return;
    setPresetId(id);
    track('mobile_preset_loaded', { presetId: id });
  }

  async function copyLink() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyState('copied');
      track('mobile_link_copied');
    } catch {
      setCopyState('failed');
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState('idle'), 2200);
  }

  // Real presets only — drop the 'custom' sentinel which has no dims.
  const presets = ROOM_PRESETS.filter((p) => p.dims && p.materials);
  const verdict = result ? RATING_INFO[result.rating] : null;

  return (
    <div
      role="dialog"
      aria-label="StudioShell mobile preview"
      style={{
        minHeight: '100vh',
        width: '100%',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient background — subtle grid + corner glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '140%',
          height: 380,
          background:
            'radial-gradient(ellipse at center top, rgba(112,148,36,0.18), transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <main
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 480,
          margin: '0 auto',
          padding: '32px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        {/* Hero */}
        <header style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Brandmark />
          <Badge color="amber" icon={<Icon icon={Monitor} size={11} />}>
            Mobile preview
          </Badge>
          <h1
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 26,
              lineHeight: 1.2,
              letterSpacing: '-0.4px',
              color: 'var(--text-primary)',
              margin: 0,
            }}
          >
            Plan your studio
            <br />
            in 60 seconds.
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              margin: 0,
              color: 'var(--text-muted)',
            }}
          >
            Tap a room preset and see how it sounds. RT60 reverb time is
            calculated live across six frequency bands. The full drag-to-place
            editor needs a desktop.
          </p>
        </header>

        {/* Preset picker */}
        <section
          aria-label="Room presets"
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <SectionLabel>Pick a room</SectionLabel>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {presets.map((preset) => {
              const Icn = lookupIcon(preset.icon);
              const isActive = preset.id === presetId;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => selectPreset(preset.id)}
                  aria-pressed={isActive}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 6,
                    padding: '12px 12px 14px',
                    borderRadius: 'var(--radius-md)',
                    background: isActive ? 'rgba(112,148,36,0.08)' : 'var(--bg-panel-2)',
                    border: isActive
                      ? '1px solid rgba(112,148,36,0.55)'
                      : '1px solid var(--border-dim)',
                    boxShadow: isActive
                      ? '0 0 20px rgba(112,148,36,0.1)'
                      : 'none',
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-sans)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition:
                      'background 150ms ease-out, border-color 150ms ease-out, box-shadow 150ms ease-out',
                  }}
                >
                  <Icn
                    width={18}
                    height={18}
                    strokeWidth={2}
                    color={isActive ? 'var(--accent-green)' : 'var(--text-muted)'}
                    aria-hidden
                  />
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: '-0.1px',
                      lineHeight: 1.2,
                      color: isActive ? 'var(--accent-green)' : 'var(--text-primary)',
                    }}
                  >
                    {preset.name}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      lineHeight: 1.4,
                      color: 'var(--text-muted)',
                    }}
                  >
                    {preset.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* RT60 readout */}
        {result && verdict && (
          <section
            aria-label="Reverb time results"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              padding: 16,
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-panel)',
              border: '1px solid var(--border-mid)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <SectionLabel>Reverb time · RT60</SectionLabel>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: verdict.color,
                  fontWeight: 600,
                }}
              >
                {verdict.label}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 44,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: verdict.color,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {result.hero.toFixed(2)}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 14,
                  color: 'var(--text-muted)',
                }}
              >
                s @ 500 Hz
              </span>
            </div>

            {/* Per-band bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {result.perBand.map((rt, i) => (
                <BandRow
                  key={i}
                  label={BAND_LABELS[i]!}
                  value={rt}
                  color={verdict.color}
                />
              ))}
            </div>
          </section>
        )}

        {/* Open on desktop CTA */}
        <section
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: 16,
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-panel-2)',
            border: '1px solid var(--border-dim)',
          }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 600,
              margin: 0,
              color: 'var(--text-primary)',
            }}
          >
            Want the full editor?
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--text-muted)',
            }}
          >
            Drag bass traps, foam panels, monitors and furniture into a
            real-time 3D room. Needs a mouse and a viewport ≥ 900 px wide.
          </p>
          <button
            type="button"
            onClick={copyLink}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              background:
                copyState === 'copied'
                  ? 'rgba(112,148,36,0.14)'
                  : 'var(--accent-green)',
              color: copyState === 'copied' ? 'var(--accent-green)' : '#fff',
              border:
                copyState === 'copied'
                  ? '1px solid rgba(112,148,36,0.45)'
                  : '1px solid transparent',
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              fontSize: 13,
              lineHeight: 1,
              cursor: 'pointer',
              transition: 'background 180ms, color 180ms, border-color 180ms',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon
              icon={copyState === 'copied' ? Check : copyState === 'failed' ? X : Link2}
              size={14}
              color={copyState === 'copied' ? 'var(--accent-green)' : '#fff'}
            />
            <span>
              {copyState === 'copied' && 'Link copied'}
              {copyState === 'failed' && 'Copy failed — select the URL'}
              {copyState === 'idle' && 'Copy link to open on desktop'}
            </span>
          </button>
          <div
            title={url}
            style={{
              padding: '7px 10px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-base)',
              border: '1px solid var(--border-dim)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-hint)',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {prettyUrl}
          </div>
          <button
            type="button"
            onClick={showAbout}
            style={{
              alignSelf: 'flex-start',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              color: 'var(--accent-cyan)',
              padding: '4px 0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>How RT60 is calculated</span>
            <Icon icon={ArrowRight} size={12} color="var(--accent-cyan)" />
          </button>
        </section>

        {/* Footer */}
        <footer
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 14,
            padding: '4px 0 12px',
            fontSize: 10,
            color: 'var(--text-hint)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}
        >
          <FooterLink
            icon={Coffee}
            href="https://ko-fi.com/studioshell"
            label="Donate"
            onClick={() => track('donation_clicked', { location: 'mobile_footer' })}
          />
        </footer>
      </main>

      <AboutModal />
    </div>
  );
}

/* ─── Per-band horizontal bar ────────────────────────────────────── */

function BandRow({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  const pct = Math.min(100, (value / BAR_MAX_RT60) * 100);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr 50px',
        alignItems: 'center',
        gap: 10,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
      }}
    >
      <span style={{ color: 'var(--text-muted)', letterSpacing: 0 }}>{label}</span>
      <div
        aria-hidden
        style={{
          height: 8,
          borderRadius: 4,
          background: 'var(--bg-base)',
          overflow: 'hidden',
          border: '1px solid var(--border-dim)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: color,
            transition: 'width 250ms ease-out',
          }}
        />
      </div>
      <span
        style={{
          color: 'var(--text-primary)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toFixed(2)}s
      </span>
    </div>
  );
}

/* ─── Section label ─────────────────────────────────────────────── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '1.8px',
        textTransform: 'uppercase',
        color: 'var(--text-hint)',
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  );
}

/* ─── Brand mark ─────────────────────────────────────────────── */

function Brandmark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-md)',
          background: 'var(--accent-green)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          fontSize: 20,
          color: '#fff',
          lineHeight: 1,
          boxShadow: '0 0 24px rgba(112, 148, 36, 0.35)',
        }}
      >
        S
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: '-0.3px',
            color: 'var(--accent-green)',
            lineHeight: 1,
          }}
        >
          StudioShell
        </div>
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            lineHeight: 1,
          }}
        >
          Acoustic Room Simulator
        </div>
      </div>
    </div>
  );
}

/* ─── Footer link ────────────────────────────────────────────── */

function FooterLink({
  icon,
  label,
  href,
  onClick,
}: {
  icon: typeof Coffee;
  label: string;
  href: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: 'var(--text-hint)',
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: 10,
        letterSpacing: '1.5px',
      }}
    >
      <Icon icon={icon} size={11} />
      <span>{label}</span>
    </a>
  );
}
